import { z } from "zod";

const youtubeVideoIdPattern = /^[A-Za-z0-9_-]{11}$/;

const youtubeImportHosts = new Set(["www.youtube.com", "m.youtube.com", "music.youtube.com"]);

export type YouTubeImportCandidate = {
  url: string;
  videoId: string;
};

export const youtubeImportInputSchema = z
  .object({
    url: z.string().trim().max(2_048),
    pageTitle: z.string().trim().max(500),
    idempotencyKey: z.uuid(),
  })
  .strict()
  .superRefine((value, context) => {
    if (!youtubeImportCandidateFromUrl(value.url)) {
      context.addIssue({
        code: "custom",
        path: ["url"],
        message: "Open an individual YouTube video before importing it.",
      });
    }
  });

export type YouTubeImportInput = z.infer<typeof youtubeImportInputSchema>;

export function youtubeImportCandidateFromUrl(rawUrl: string): YouTubeImportCandidate | null {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }
  if (
    url.protocol !== "https:" ||
    url.username ||
    url.password ||
    url.port ||
    !youtubeImportHosts.has(url.hostname.toLocaleLowerCase("en"))
  ) {
    return null;
  }

  let videoId: string | null = null;
  if (url.pathname === "/watch" && url.searchParams.getAll("v").length === 1) {
    videoId = url.searchParams.get("v");
  } else {
    videoId = /^\/(?:shorts|live)\/([A-Za-z0-9_-]{11})\/?$/.exec(url.pathname)?.[1] ?? null;
  }
  if (!videoId || !youtubeVideoIdPattern.test(videoId)) return null;

  url.hash = "";
  return { url: url.toString(), videoId };
}

export function youtubeVideoIdFromUrl(rawUrl: string): string | null {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }
  if ((url.protocol !== "https:" && url.protocol !== "http:") || url.username || url.password) {
    return null;
  }

  const hostname = url.hostname.toLocaleLowerCase("en").replace(/^www\./, "");
  let candidate: string | null = null;
  if (hostname === "youtu.be") {
    candidate = url.pathname.split("/").filter(Boolean)[0] ?? null;
  } else if (
    hostname === "youtube.com" ||
    hostname === "m.youtube.com" ||
    hostname === "music.youtube.com"
  ) {
    candidate =
      url.pathname === "/watch"
        ? url.searchParams.get("v")
        : (url.pathname.match(/^\/(?:embed|live|shorts)\/([^/]+)/)?.[1] ?? null);
  } else if (hostname === "youtube-nocookie.com") {
    candidate = url.pathname.match(/^\/embed\/([^/]+)/)?.[1] ?? null;
  }

  return candidate && youtubeVideoIdPattern.test(candidate) ? candidate : null;
}

export function youtubeEmbedUrl(rawUrl: string): string | null {
  const videoId = youtubeVideoIdFromUrl(rawUrl);
  return videoId ? `https://www.youtube-nocookie.com/embed/${videoId}` : null;
}

export function youtubeThumbnailUrl(videoId: string): string | null {
  return youtubeVideoIdPattern.test(videoId)
    ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
    : null;
}
